import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import toast from "react-hot-toast";
import { UserCreate } from "services/apiService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

interface FormData {
  full_name: string;
  email: string;
  phone_no: string;
  password: string;
}

interface FormErrors extends Partial<FormData> {}

interface CustomersModalProps {
  open: boolean;
  onClose: () => void;
}

const CustomersCreate: React.FC<CustomersModalProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    full_name: "",
    email: "",
    phone_no: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false); // Eye Icon Toggle

  useEffect(() => {
    if (!open) {
      setErrors({});
      setFormData({
        full_name: "",
        email: "",
        phone_no: "",
        password: "",
      });
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const { full_name, email, phone_no, password } = formData;

    if (!full_name.trim()) newErrors.full_name = "Full Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Invalid email format";

    if (!phone_no.trim()) newErrors.phone_no = "Phone number is required";
    else if (!/^\d{10}$/.test(phone_no))
      newErrors.phone_no = "Must be 10 digits";

    if (!password.trim()) newErrors.password = "Password is required";
    else {
      if (password.length < 6)
        newErrors.password = "At least 6 characters required";
      if (!/[A-Z]/.test(password))
        newErrors.password = "Must contain an uppercase letter";
      if (!/[a-z]/.test(password))
        newErrors.password = "Must contain a lowercase letter";
      if (!/[0-9]/.test(password)) newErrors.password = "Must contain a number";
      if (!/[!@#$%^&*]/.test(password))
        newErrors.password = "Must contain a special character";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        phone_no: Number(formData.phone_no),
        email_verify: 1,
        is_active: 1,
      };
      const response = await UserCreate(payload);
      if (response.status_code === 200) {
        toast.success("User created successfully");
        onClose();
      } else {
        toast.error("User creation failed, try again");
      }
    } catch (error) {
      toast.error(
        "User creation failed, Give unique name and mail id , and fill the form ,try again"
      );
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (name === "full_name" && value.trim()) {
        delete newErrors.full_name;
      }
      if (name === "email") {
        if (value.trim() && /\S+@\S+\.\S+/.test(value)) {
          delete newErrors.email;
        }
      }
      if (name === "phone_no") {
        if (/^\d{10}$/.test(value)) delete newErrors.phone_no;
      }
      if (name === "password") {
        if (
          value.length >= 6 &&
          /[A-Z]/.test(value) &&
          /[a-z]/.test(value) &&
          /[0-9]/.test(value) &&
          /[!@#$%^&*]/.test(value)
        ) {
          delete newErrors.password;
        }
      }
      return newErrors;
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: 400,
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" mb={2}>
          Create New Customer
        </Typography>

        <form onSubmit={handleSubmit}>
          {["full_name", "email", "phone_no"].map((field, idx) => (
            <TextField
              key={idx}
              fullWidth
              label={field
                .replace("_", " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
              name={field}
              type="text"
              value={formData[field as keyof FormData]}
              onChange={handleChange}
              error={!!errors[field as keyof FormErrors]}
              helperText={
                field === "email"
                  ? errors[field as keyof FormErrors] || "Enter a unique email"
                  : field === "full_name"
                  ? errors[field as keyof FormErrors] ||
                    "Enter a unique username"
                  : errors[field as keyof FormErrors]
              }
              margin="normal"
            />
          ))}
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? (
                      <FontAwesomeIcon icon={faEyeSlash} />
                    ) : (
                      <FontAwesomeIcon icon={faEye} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            Add User
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

export default CustomersCreate;
